import { css } from 'styled-components';

/**
 * 관리자 페이지 공용 디자인 토큰 — 레거시(클래식) 톤.
 *
 * 이 리포 다수 관리자 페이지(쿠폰·매니저·회원·공지·로그인)가 쓰는 톤을 토큰화한 것이다.
 * 값은 새로 디자인한 것이 아니라 아래 실측 앵커에서 뽑았다:
 * - 페이지 배경 `#f0f0f0` — css/dashboard.css `.content-container`
 * - 카드 라운드/그림자 — page/login.js (radius 12px, 은은한 그림자)
 * - 버튼 radius 8px · `#007bff`/hover `#0056b3` · Danger `#d32f2f` — management/manager/manager.js
 * - 입력 radius 6px · `1px #ddd` · 포커스 링 `rgba(0,123,255,0.1)` — manager.js / login.js
 * - 액센트는 상단 메뉴(component/main_menu.js `#007bff`)와 동일 계열
 *
 * export API는 admin_flat_theme.js와 동일하다(color·font·space·layout·mixin·toneColor).
 * 두 모듈은 값만 다르므로 페이지는 import 한 줄로 톤을 바꾼다 — 색 리터럴은 이 파일에서만
 * 정의하고 페이지 파일은 토큰만 참조한다.
 */

export const color = {
    // 표면
    page: '#f0f0f0',
    surface: '#ffffff',
    surfaceMuted: '#f7f8fa',
    scrim: 'rgba(255, 255, 255, 0.92)',

    // 경계선
    line: '#e8e8e8',
    lineStrong: '#dddddd',

    // 타이포 (농도 4단)
    textStrong: '#333333',
    text: '#444444',
    textMuted: '#666666',
    textFaint: '#999999',

    // 액센트 (상단 메뉴와 동일 계열)
    accent: '#007bff',
    accentStrong: '#0056b3',
    accentSoft: 'rgba(0, 123, 255, 0.1)',
    onAccent: '#ffffff',

    // 시맨틱
    success: '#1e8e3e',
    warning: '#b26a00',
    danger: '#d32f2f',
    dangerStrong: '#b71c1c'
};

const STATUS_COLOR = {
    live: color.success,
    scheduled: color.accent,
    expired: color.warning,
    disabled: color.textMuted,
    incomplete: color.danger,
    neutral: color.accent,
    warning: color.warning,
    ended: color.textMuted
};

/** 팝업 노출 상태·이벤트 상태 톤을 단일 팔레트로 매핑한다. */
export const toneColor = (tone) => STATUS_COLOR[tone] || color.textMuted;

/** 모서리 반경 — 레거시 톤의 핵심 특징이라 토큰으로 고정한다. */
export const radius = {
    card: '12px',
    control: '8px',
    field: '6px',
    pill: '999px'
};

export const shadow = {
    card: '0 2px 8px rgba(0, 0, 0, 0.06)',
    raised: '0 4px 16px rgba(0, 0, 0, 0.1)'
};

/** 타이포 위계 — 레거시 페이지의 rem 스케일을 따른다. */
export const font = {
    pageTitle: css`
        font-size: 1.4rem;
        font-weight: 700;
        line-height: 1.3;
    `,
    sectionTitle: css`
        font-size: 1.05rem;
        font-weight: 700;
        line-height: 1.4;
    `,
    label: css`
        font-size: 0.9rem;
        font-weight: 500;
        line-height: 1.45;
    `,
    body: css`
        font-size: 0.95rem;
        font-weight: 400;
        line-height: 1.6;
    `,
    caption: css`
        font-size: 0.85rem;
        font-weight: 400;
        line-height: 1.55;
    `,
    eyebrow: css`
        font-size: 0.75rem;
        font-weight: 700;
        line-height: 1.4;
        letter-spacing: 0.04em;
    `,
    mono: css`
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
        font-variant-numeric: tabular-nums;
    `
};

/** 간격 리듬 — 4의 배수. */
export const space = {
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '24px',
    xxl: '32px',
    xxxl: '48px'
};

export const layout = {
    maxWidth: '1240px',
    pagePadding: '28px clamp(16px, 3vw, 40px) 56px',
    pagePaddingSmall: '20px 12px 40px'
};

export const mixin = {
    /** 흰 카드 — 라운드 + 은은한 그림자(레거시 관리자 카드의 기본형). */
    panel: css`
        background: ${color.surface};
        border-radius: ${radius.card};
        box-shadow: ${shadow.card};
    `,
    hairlineTop: css`
        border-top: 1px solid ${color.line};
    `,
    focusRing: css`
        &:focus-visible {
            outline: 2px solid ${color.accent};
            outline-offset: 2px;
        }
    `,
    /** 입력 요소 공통 — radius 6px · 1px 경계선 · 포커스 링. */
    field: css`
        box-sizing: border-box;
        width: 100%;
        min-height: 42px;
        padding: 0 12px;
        border: 1px solid ${color.lineStrong};
        border-radius: ${radius.field};
        background: ${color.surface};
        color: ${color.text};
        font: inherit;
        font-size: 0.95rem;

        &::placeholder {
            color: ${color.textFaint};
        }

        &:hover:not(:disabled) {
            border-color: ${color.textFaint};
        }

        &:focus {
            outline: none;
            border-color: ${color.accent};
            box-shadow: 0 0 0 3px ${color.accentSoft};
        }

        &:disabled {
            border-color: ${color.line};
            background: ${color.surfaceMuted};
            color: ${color.textFaint};
            cursor: not-allowed;
        }
    `,
    buttonBase: css`
        display: inline-flex;
        align-items: center;
        justify-content: center;
        height: 40px;
        padding: 0 18px;
        border: 1px solid transparent;
        border-radius: ${radius.control};
        font-size: 0.9rem;
        font-weight: 600;
        line-height: 1;
        cursor: pointer;
        transition: background-color 0.2s, border-color 0.2s;

        &:focus-visible {
            outline: 2px solid ${color.accent};
            outline-offset: 2px;
        }

        &:disabled {
            cursor: not-allowed;
        }
    `,
    buttonPrimary: css`
        border-color: ${color.accent};
        background: ${color.accent};
        color: ${color.onAccent};

        &:hover:not(:disabled) {
            border-color: ${color.accentStrong};
            background: ${color.accentStrong};
        }

        &:disabled {
            border-color: ${color.line};
            background: ${color.surfaceMuted};
            color: ${color.textFaint};
        }
    `,
    buttonGhost: css`
        border-color: #cccccc;
        background: ${color.surface};
        color: ${color.textMuted};

        &:hover:not(:disabled) {
            border-color: ${color.textFaint};
            background: ${color.surfaceMuted};
        }

        &:disabled {
            border-color: ${color.line};
            background: ${color.surfaceMuted};
            color: ${color.textFaint};
        }
    `,
    buttonDanger: css`
        border-color: ${color.danger};
        background: ${color.danger};
        color: ${color.onAccent};

        &:hover:not(:disabled) {
            border-color: ${color.dangerStrong};
            background: ${color.dangerStrong};
        }

        &:disabled {
            border-color: ${color.line};
            background: ${color.surfaceMuted};
            color: ${color.textFaint};
        }
    `,
    /** 상태 칩 — 라운드 알약 + 연한 경계선. */
    statusChip: css`
        display: inline-flex;
        align-items: center;
        gap: 6px;
        flex: 0 0 auto;
        padding: 4px 10px;
        border: 1px solid ${color.line};
        border-radius: ${radius.pill};
        background: ${color.surface};
        font-size: 0.75rem;
        font-weight: 700;
        line-height: 1.3;
        white-space: nowrap;
    `,
    /** 상태 마크(도트) — 부모의 currentColor를 쓴다. */
    dot: css`
        flex: 0 0 auto;
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: currentColor;
    `,
    switchTrack: css`
        position: relative;
        flex: 0 0 auto;
        width: 46px;
        height: 26px;
        padding: 0;
        border: 1px solid transparent;
        border-radius: ${radius.pill};
        cursor: pointer;
        transition: background 140ms ease, border-color 140ms ease;
    `,
    switchKnob: css`
        position: absolute;
        top: 3px;
        width: 18px;
        height: 18px;
        border-radius: 50%;
        background: ${color.surface};
        transition: left 140ms ease;
    `,
    /** 표 공통 — 헤더만 연회색, 행 구분은 헤어라인. */
    table: css`
        width: 100%;
        border-collapse: collapse;
        color: ${color.text};
        font-size: 0.85rem;

        th,
        td {
            padding: 10px 12px;
            border-bottom: 1px solid ${color.line};
            text-align: left;
            white-space: nowrap;
        }

        th {
            background: ${color.surfaceMuted};
            color: ${color.textMuted};
            font-size: 0.8rem;
            font-weight: 700;
        }

        tbody tr:last-child td {
            border-bottom: 0;
        }
    `
};
